import React, { useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import Header from '../../components/common/global-top-bar';
import Spinner from '../../components/common/spinner';
import { useSettings, useUpdateSettings } from '../../hooks/useSettings';
import { createDefaultValues, TextBlocksHeaderOptions } from './common/text-blocks-constants';
import SwitchBar from '../../components/switch-bar';

const CRISIS_MESSAGE_KEYS = [
  'CRISIS_PENDING_MESSAGE',
  'CRISIS_REJECTED_MESSAGE',
  'CRISIS_CONFIRMED_MESSAGE',
  'CRISIS_ESCALATED_MESSAGE',
] as const;

type CrisisMessageKey = (typeof CRISIS_MESSAGE_KEYS)[number];

export const PeersonaCrisisDefaults = () => {
  const { data: appSettings, isLoading } = useSettings('email');
  const updateSettings = useUpdateSettings();

  const defaultCrisisMessages = useMemo(() => createDefaultValues(CRISIS_MESSAGE_KEYS, appSettings), [appSettings]);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { isValid },
  } = useForm({
    defaultValues: defaultCrisisMessages,
  });

  const cancelRef = useRef() as React.MutableRefObject<HTMLButtonElement>;

  useEffect(() => {
    if (defaultCrisisMessages) {
      reset(defaultCrisisMessages);
    }
  }, [defaultCrisisMessages, reset]);

  const onSubmit = (data: any) => {
    const payload = {
      data: {
        message: appSettings?.message,
        system: { ...appSettings?.system, ...data },
      },
    };

    updateSettings.mutate(payload, {
      onSuccess: () => {
        toast.success('Crisis Defaults updated successfully!');
        cancelRef.current.classList.remove('bg-red');
        cancelRef.current.classList.add('bg-gray-background-dark');
      },
      onError: () => {
        toast.error('Failed to update Crisis Defaults. Please try again.');
      },
    });
  };

  const handleCancel = () => {
    reset(defaultCrisisMessages);
    cancelRef.current.classList.remove('bg-red');
    cancelRef.current.classList.add('bg-gray-background-dark');
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>, key: CrisisMessageKey) => {
    setValue(key, e.target.value);
    cancelRef.current.classList.remove('bg-gray-background-dark');
    cancelRef.current.classList.add('bg-red');
  };

  return (
    <div>
      <Header heading={'Crisis Defaults Settings'} />
      <SwitchBar heading={TextBlocksHeaderOptions} position={6} />
      <div className="max-window-height overflow-y-auto pb-10">
        <div className="w-full px-7">
          {isLoading && <Spinner />}
          {!isLoading &&
            !appSettings?.system?.CRISIS_PENDING_MESSAGE &&
            !appSettings?.system?.CRISIS_REJECTED_MESSAGE &&
            !appSettings?.system?.CRISIS_CONFIRMED_MESSAGE &&
            !appSettings?.system?.CRISIS_ESCALATED_MESSAGE && (
              <div className="mt-4 rounded-md border border-yellow-200 bg-yellow-50 p-4">
                <p className="text-yellow-800">
                  No Crisis Defaults are currently configured. Use the textareas below to add them.
                </p>
              </div>
            )}
          <div className="mt-6">
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="mb-8">
                <h2 className="mb-4 border-b border-gray-200 pb-2 text-xl font-semibold text-gray-800">
                  Crisis Defaults
                </h2>
                <p className="mb-6 text-sm text-gray-600">
                  These defaults display as the Peersona's response messages during crisis response.
                </p>
              </div>

              <div className="mb-4">
                <label htmlFor="CRISIS_PENDING_MESSAGE" className="mb-2 block text-sm font-medium text-gray-700">
                  Crisis Pending Message
                </label>
                <p className="mb-2 text-xs text-gray-500">
                  This message is displayed when the Peersona first detects a crisis and is waiting for the user to
                  respond. Yes/No buttons are available to the user in place of a text input.
                </p>
                <textarea
                  id="CRISIS_PENDING_MESSAGE"
                  {...register('CRISIS_PENDING_MESSAGE')}
                  onChange={(e) => handleTextareaChange(e, 'CRISIS_PENDING_MESSAGE')}
                  className="focus:ring-bright-blue focus:border-bright-blue resize-vertical h-64 w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none"
                  placeholder="Enter the crisis pending message..."
                  data-testid="crisis-pending-message-textarea"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="CRISIS_REJECTED_MESSAGE" className="mb-2 block text-sm font-medium text-gray-700">
                  Crisis Rejected Message
                </label>
                <p className="mb-2 text-xs text-gray-500">
                  This message is displayed when the user responds "No" to the crisis pending message. Chat resumes
                  after this message.
                </p>
                <textarea
                  id="CRISIS_REJECTED_MESSAGE"
                  {...register('CRISIS_REJECTED_MESSAGE')}
                  onChange={(e) => handleTextareaChange(e, 'CRISIS_REJECTED_MESSAGE')}
                  className="focus:ring-bright-blue focus:border-bright-blue resize-vertical h-64 w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none"
                  placeholder="Enter the crisis rejected message..."
                  data-testid="crisis-rejected-message-textarea"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="CRISIS_CONFIRMED_MESSAGE" className="mb-2 block text-sm font-medium text-gray-700">
                  Crisis Confirmed Message
                </label>
                <p className="mb-2 text-xs text-gray-500">
                  This message is displayed when the user responds "Yes" to the crisis pending message. Chat is put into
                  cooldown after this message, and we display a Crisis Card below this message.
                </p>
                <textarea
                  id="CRISIS_CONFIRMED_MESSAGE"
                  {...register('CRISIS_CONFIRMED_MESSAGE')}
                  onChange={(e) => handleTextareaChange(e, 'CRISIS_CONFIRMED_MESSAGE')}
                  className="focus:ring-bright-blue focus:border-bright-blue resize-vertical h-64 w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none"
                  placeholder="Enter the crisis confirmed message..."
                  data-testid="crisis-confirmed-message-textarea"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="CRISIS_ESCALATED_MESSAGE" className="mb-2 block text-sm font-medium text-gray-700">
                  Crisis Escalated Message
                </label>
                <p className="mb-2 text-xs text-gray-500">
                  This message is displayed when the crisis is detected a second time during the same conversation. Chat
                  is put into cooldown after this message, and we display a Crisis Card below this message.
                </p>
                <textarea
                  id="CRISIS_ESCALATED_MESSAGE"
                  {...register('CRISIS_ESCALATED_MESSAGE')}
                  onChange={(e) => handleTextareaChange(e, 'CRISIS_ESCALATED_MESSAGE')}
                  className="focus:ring-bright-blue focus:border-bright-blue resize-vertical h-64 w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none"
                  placeholder="Enter the crisis escalated message..."
                  data-testid="crisis-escalated-message-textarea"
                />
              </div>

              <div className="flex justify-end space-x-4">
                <button
                  type="submit"
                  disabled={!isValid}
                  className="bg-bright-blue rounded-full px-6 py-2 font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-400"
                  data-testid="crisis-defaults-save-button"
                >
                  {updateSettings.isLoading ? 'Saving...' : 'Save'}
                </button>

                <button
                  type="button"
                  ref={cancelRef}
                  onClick={handleCancel}
                  className="bg-gray-background-dark rounded-full px-6 py-2 font-medium text-white transition-colors hover:bg-gray-600"
                  data-testid="crisis-defaults-cancel-button"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};
